import React, { useCallback, useEffect, useState } from 'react'
import { BillingService } from '../../billing.service'

// Human labels for quota_transactions.source_type.
const SOURCE_LABELS = {
  reservation_captured: 'Screening usage',
  invoice_paid: 'Paid invoice',
  manual_adjust: 'Manual adjustment',
  refund: 'Refund',
  // Free quota forfeited when the subscription that granted it ended (BR-KUOTA-08).
  expiry_forfeit: 'Forfeited quota',
}

function formatDateTime(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) +
    ', ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
}

function EnquiryRef({ enquiry }) {
  if (!enquiry) return null
  return (
    <span className="text-xs-regular text-gray-light/600">
      {enquiry.reference_number}
      {enquiry.subject ? ` · ${enquiry.subject}` : ''}
    </span>
  )
}

// Read-only "Riwayat Penggunaan Saldo/Kuota". Two views:
//   - Riwayat: the quota ledger (top-up / charge) with the running balance.
//   - Sedang di-hold: quota reserved for in-flight enquiries, not yet charged.
// Self-contained — takes only companyId so it can be mounted anywhere once the
// UI/UX team decides the entrypoint (currently rendered inside the Billing panel).
export default function QuotaUsage({ companyId }) {
  const [tab, setTab] = useState('ledger')
  const [ledger, setLedger] = useState([])
  const [paginator, setPaginator] = useState(null)
  const [holds, setHolds] = useState([])
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const loadLedger = useCallback(
    (p) => {
      if (!companyId) return
      setLoading(true)
      setError(null)
      BillingService.getQuotaLedger(companyId, { page: p, limit: 10 })
        .then((res) => {
          setLedger(res?.data?.data ?? res?.data ?? [])
          setPaginator(res?.paginator ?? null)
        })
        .catch((e) => setError(e?.message || 'Failed to load history.'))
        .finally(() => setLoading(false))
    },
    [companyId],
  )

  useEffect(() => {
    loadLedger(page)
  }, [loadLedger, page])

  useEffect(() => {
    if (!companyId) return
    BillingService.getQuotaHolds(companyId)
      .then((res) => setHolds(res?.data?.data ?? res?.data ?? []))
      .catch(() => {})
  }, [companyId])

  const tabs = [
    { key: 'ledger', label: 'Transaction history' },
    { key: 'holds', label: `On hold${holds.length ? ` (${holds.length})` : ''}` },
  ]

  return (
    <section className="rounded-xl border border-gray-light/200 bg-white shadow-xs">
      <div className="border-b border-gray-light/200 px-4 py-3">
        <p className="text-sm-semibold text-gray-light/900">Quota usage history</p>
        <p className="text-xs-regular text-gray-light/600">
          Transactions that add to or deduct from your quota, and quota currently on hold.
        </p>
      </div>

      <div className="flex gap-1 px-4 pt-3">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`rounded-lg px-3 py-1.5 text-sm-medium transition-colors ${
              tab === t.key
                ? 'bg-brand/50 text-brand/700'
                : 'text-gray-light/600 hover:bg-gray-light/50'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="p-4">
        {error && <p className="text-sm-regular text-error/600">{error}</p>}

        {tab === 'ledger' && (
          <>
            {loading && !ledger.length ? (
              <p className="py-6 text-center text-sm-regular text-gray-light/500">Loading…</p>
            ) : !ledger.length ? (
              <p className="py-6 text-center text-sm-regular text-gray-light/500">
                No quota transactions yet.
              </p>
            ) : (
              <div className="flex flex-col divide-y divide-gray-light/200">
                {ledger.map((row) => {
                  const isCredit = row.type === 'credit'
                  return (
                    <div key={row.id} className="flex items-start justify-between gap-3 py-3">
                      <div className="flex min-w-0 flex-col">
                        <span className="text-sm-medium text-gray-light/900">
                          {SOURCE_LABELS[row.source_type] || row.source_type}
                        </span>
                        <EnquiryRef enquiry={row.enquiry} />
                        <span className="text-xs-regular text-gray-light/500">
                          {formatDateTime(row.created_at)}
                        </span>
                      </div>
                      <div className="flex shrink-0 flex-col items-end">
                        <span
                          className={`text-sm-semibold ${
                            isCredit ? 'text-success/600' : 'text-error/600'
                          }`}
                        >
                          {isCredit ? '+' : '−'}
                          {row.amount} quota
                        </span>
                        {row.balance_after != null && (
                          <span className="text-xs-regular text-gray-light/500">
                            Remaining: {row.balance_after}
                          </span>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {paginator && paginator.total_pages > 1 && (
              <div className="mt-3 flex items-center justify-between">
                <span className="text-xs-regular text-gray-light/600">
                  Page {paginator.page} of {paginator.total_pages}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="rounded-lg border border-gray-light/300 px-3 py-1 text-sm-medium text-gray-light/700 disabled:opacity-40"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={page >= paginator.total_pages || loading}
                    onClick={() => setPage((p) => p + 1)}
                    className="rounded-lg border border-gray-light/300 px-3 py-1 text-sm-medium text-gray-light/700 disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {tab === 'holds' && (
          !holds.length ? (
            <p className="py-6 text-center text-sm-regular text-gray-light/500">
              No quota on hold.
            </p>
          ) : (
            <div className="flex flex-col divide-y divide-gray-light/200">
              {holds.map((h) => (
                <div key={h.id} className="flex items-start justify-between gap-3 py-3">
                  <div className="flex min-w-0 flex-col">
                    <span className="text-sm-medium text-gray-light/900">
                      {h.enquiry?.reference_number || '—'}
                      {h.enquiry?.subject ? ` · ${h.enquiry.subject}` : ''}
                    </span>
                    <span className="text-xs-regular text-gray-light/500">
                      On hold since {formatDateTime(h.reserved_at)}
                    </span>
                  </div>
                  <span className="shrink-0 text-sm-semibold text-warning/700">
                    {h.amount} quota
                  </span>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </section>
  )
}
